import React from 'react';
import { Navigate, NavLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicDonate } from '../../contexts/PublicDonateContext';
import { formatCurrency } from '../portal/portalDonateShared';
import { cartLineOutOfTownSurcharge, cartMerchandiseLineTotal, normalizeOutOfTownDays } from '../../utils/outOfTownSurcharge';

export default function PublicDonateCartPage() {
  const navigate = useNavigate();
  const { cart, totalAmount, merchandiseSubtotal, outOfTownSubtotal, updateQuantity, removeItem, itemCount, donateMode } =
    usePublicDonate();
  const { isAuthenticated, loading: authLoading, staff } = useAuth();
  const hasPortalSession = !authLoading && isAuthenticated && staff?.portal;
  const [error, setError] = React.useState('');

  if (!donateMode && authLoading) {
    return (
      <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!donateMode && !hasPortalSession) {
    return <Navigate to="/donate" replace />;
  }

  const onCheckout = () => {
    if (!cart.length) {
      setError('Cart is empty');
      return;
    }
    navigate('/donate/checkout');
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>Donate cart</Typography>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {itemCount} item{itemCount === 1 ? '' : 's'} in cart
          </Typography>
        </CardContent>
      </Card>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {cart.length === 0 ? (
        <Stack spacing={2} alignItems="flex-start">
          <Alert severity="info" sx={{ width: '100%' }}>Your cart is empty. Add products from Donate.</Alert>
          <Button variant="outlined" component={NavLink} to="/donate">
            Continue shopping
          </Button>
        </Stack>
      ) : (
        <>
          {cart.map((item) => {
            const key = `${item.product_id}-${item.variation_id || 0}`;
            const qty = Math.max(1, parseInt(item.quantity, 10) || 1);
            const lineTotal = cartMerchandiseLineTotal(item);
            const travel = cartLineOutOfTownSurcharge(item);
            return (
              <Card key={key} variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {item.product_name || `Product #${item.product_id}`}
                      </Typography>
                      {item.variation_name ? (
                        <Typography variant="body2" color="text.secondary">
                          {item.variation_name}
                        </Typography>
                      ) : null}
                      <Typography variant="body2" color="text.secondary">
                        Unit: {formatCurrency(item.unit_price)}
                      </Typography>
                      {normalizeOutOfTownDays(item.out_of_town_days) > 0 ? (
                        <Typography variant="caption" sx={{ color: '#9a5500', display: 'block' }}>
                          Outside Shelby County — {normalizeOutOfTownDays(item.out_of_town_days)} day
                          {normalizeOutOfTownDays(item.out_of_town_days) === 1 ? '' : 's'} (+{formatCurrency(travel)})
                        </Typography>
                      ) : null}
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        label="Qty"
                        size="small"
                        type="number"
                        value={qty}
                        inputProps={{ min: 1, step: 1 }}
                        onChange={(e) => updateQuantity(item.product_id, item.variation_id || 0, e.target.value)}
                        sx={{ width: 90 }}
                      />
                      <Typography sx={{ minWidth: 100, textAlign: 'right', fontWeight: 700 }}>
                        {formatCurrency(lineTotal + travel)}
                      </Typography>
                      <IconButton
                        color="error"
                        onClick={() => removeItem(item.product_id, item.variation_id || 0)}
                        aria-label="Remove item"
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1">Service subtotal</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {formatCurrency(merchandiseSubtotal)}
                  </Typography>
                </Stack>
                {outOfTownSubtotal > 0.001 ? (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1" color="text.secondary">
                      Outside of Shelby County
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {formatCurrency(outOfTownSubtotal)}
                    </Typography>
                  </Stack>
                ) : null}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">Subtotal</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {formatCurrency(totalAmount)}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
            <Button variant="outlined" component={NavLink} to="/donate">
              Continue shopping
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={onCheckout}
              sx={{ background: 'linear-gradient(135deg, #1a237e 0%, #3141b7 100%)' }}
            >
              Checkout
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
}
